import { ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { CHAT_AUTH, CHAT_CONFIG } from 'shared-chat';
import { DOCS_CONFIG } from 'shared-docs';
import { NOTIFICATION_AUTH, NOTIFICATION_CONFIG, NotificationConfig } from 'shared-notifications';
import { SUPPORT_AUTH, SUPPORT_CONFIG } from 'shared-support';
import {
  DOCUMENTS_TABLE_CONFIG,
  DocumentsTableConfig,
  RECAPTCHA_SITE_KEY,
  SettingKey,
  SettingScope,
  USER_PROFILE_CONFIG,
  USER_TRANSFER_CONFIG,
  USER_WALLET_CONFIG,
  UserProfileConfig,
  UserTransferConfig,
  UserWalletConfig,
} from 'shared-components';
import { map } from 'rxjs';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { AuthService } from './core/services/auth.service';
import { FileUploadService } from './core/services/file-upload.service';
import { UsersService } from './core/services/users.service';
import { CompanyUserSettingsService } from './core/services/company-user-settings.service';
import { DriverTransferService } from './core/services/driver-transfer.service';
import { CompanyPaymentService } from './core/services/company-payment.service';
import { UserSetting } from './core/interfaces';
import { getDefaultLanguage } from './core/types/language.types';
import { SimplifiedTranslateLoader } from './core/services/simplified-translate-loader.service';
import { TranslationCacheService } from './core/services/translation-cache.service';
import { ChunkLoadErrorHandler } from './core/services/chunk-load-error-handler.service';

export function documentsTableConfigFactory(http: HttpClient, fileUploadService: FileUploadService): DocumentsTableConfig {
  return {
    listDocuments: (userId) => fileUploadService.getUserDocuments(userId),
    uploadDocument: (userId, file) => {
      const formData = new FormData();
      formData.append('file', file);
      return http.post<{ success: boolean; data: unknown }>(
        `${environment.apiUrl}/company/files/user/${userId}/documents`,
        formData,
        { reportProgress: true, observe: 'events' },
      ) as ReturnType<DocumentsTableConfig['uploadDocument']>;
    },
    deleteDocument: (fileId) => fileUploadService.deleteFile(fileId),
    getSignedUrl: (fileId) => fileUploadService.getSignedUrl(fileId),
    getFileContent: (fileId) => fileUploadService.getFileContent(fileId),
  };
}

// CompanyUserSettingsService returns one merged UserSetting[] (value +
// settingKey metadata together) - SettingsFormConfig (bab-settings-form,
// used by the shared bab-user-view's Settings tab) expects two separate
// calls instead (values-only + keys-only). Same split-adapter shape
// ExecutorSettingsService already uses for the exact same reason.
function toSettingKey(index: number, info: UserSetting['settingKey']): SettingKey {
  return {
    id: index,
    keyName: info?.slug ?? '',
    slug: info?.slug ?? '',
    description: info?.description,
    keyType: (info?.keyType ?? 'text') as SettingKey['keyType'],
    scope: SettingScope.USER,
    options: info?.options,
    defaultValue: info?.defaultValue,
    validationRules: info?.validationRules,
    isActive: true,
    isRequired: !!info?.isRequired,
    sortOrder: index,
    groupName: info?.groupName,
    unit: info?.unit,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

export function userProfileConfigFactory(usersService: UsersService, settingsService: CompanyUserSettingsService): UserProfileConfig {
  return {
    getSelf: () => usersService.getProfile(),
    updateSelf: (data) => usersService.updateProfile(data),
    getById: (id) => usersService.getUser(id),
    updateById: (id, data) => usersService.updateUser(id, data),
    deleteById: (id) => usersService.deleteUser(id),
    sendPasswordResetEmail: (id) => usersService.sendPasswordResetEmail(id),
    settingsScope: SettingScope.USER,
    getSettings: (entityId) =>
      settingsService.getSettings(entityId, 'user').pipe(
        map((res) => ({ success: res.success, data: Object.fromEntries((res.data ?? []).map((s) => [s.key, s.value])) })),
      ),
    getSettingKeys: (entityId) =>
      settingsService.getSettings(entityId, 'user').pipe(
        map((res) => ({ success: res.success, data: (res.data ?? []).map((s, i) => toSettingKey(i, s.settingKey)) })),
      ),
    updateSettings: (entityId, settings) =>
      settingsService.bulkUpsertSettings(entityId, settings).pipe(
        map((res) => ({ success: res.success, data: Object.fromEntries((res.data ?? []).map((s) => [s.key, s.value])) })),
      ),
  };
}

export function userTransferConfigFactory(transferService: DriverTransferService): UserTransferConfig {
  return {
    getAvailableCompanies: (excludeCompanyId) => transferService.getAvailableCompanies(excludeCompanyId),
    getHistory: (subjectId) => transferService.getHistory(subjectId),
    validateTransfer: (subjectId) => transferService.validateTransfer(subjectId),
    transfer: (subjectId, dto) => transferService.transfer(subjectId, dto),
    dismiss: (subjectId, dto) => transferService.dismiss(subjectId, dto),
    validateUndoTransfer: (subjectId) => transferService.validateUndoTransfer(subjectId),
    undoTransfer: (subjectId) => transferService.undoTransfer(subjectId),
    validateUndoDismiss: (subjectId) => transferService.validateUndoDismiss(subjectId),
    undoDismiss: (subjectId) => transferService.undoDismiss(subjectId),
  };
}

export function userWalletConfigFactory(paymentService: CompanyPaymentService): UserWalletConfig {
  return {
    getBalance: (subjectId, companyId) =>
      paymentService.getExecutorWallet(subjectId, companyId).pipe(
        map((res) => ({ success: res.success, data: { balance: Number(res.data?.balance ?? 0) } })),
      ),
    getTransactions: (subjectId, query, companyId) =>
      paymentService.getExecutorWalletTransactions(subjectId, query, companyId).pipe(
        map((res) => {
          const payload = res.data ?? {};
          return {
            success: res.success,
            data: { transactions: payload.transactions ?? (Array.isArray(payload) ? payload : []), total: payload.total ?? 0 },
          };
        }),
      ),
    manualCredit: (subjectId, dto, companyId) => paymentService.manualCreditExecutor(subjectId, dto, companyId),
    manualDebit: (subjectId, dto, companyId) => paymentService.manualDebitExecutor(subjectId, dto, companyId),
  };
}

/**
 * Simplified translation loader factory
 * Loads ONLY core modules immediately
 * Page modules are loaded on-demand via RouteTranslationResolver
 */
export function SimplifiedLoaderFactory(http: HttpClient, cache: TranslationCacheService) {
  return new SimplifiedTranslateLoader(http, cache);
}

/**
 * Support-ticket push events already exist on the backend (SupportNotificationService ->
 * WebSocketClient.broadcastToUser) - this just surfaces them in the shared header bell,
 * which nothing listened for before. Company acts as both requester (its own escalations
 * to admin) and responder (assigned/transferred operator), so both directions matter here.
 */
export function notificationConfigFactory(): NotificationConfig {
  const ticketRoute = (data: any) => ['/support', String(data.ticketId)];
  return {
    socketUrl: environment.socketUrl ? `${environment.socketUrl}/ws` : undefined,
    events: {
      'support-ticket-assigned': (data) => ({
        titleKey: 'header.notif_support_ticket_title',
        bodyKey: 'header.notif_support_ticket_body',
        bodyParams: { ticketNumber: data.ticketNumber, status: 'assigned' },
        route: ticketRoute(data),
      }),
      'support-ticket-status-changed': (data) => ({
        titleKey: 'header.notif_support_ticket_title',
        bodyKey: 'header.notif_support_ticket_body',
        bodyParams: { ticketNumber: data.ticketNumber, status: data.statusSlug },
        route: ticketRoute(data),
      }),
      'support-ticket-transferred': (data) => ({
        titleKey: 'header.notif_support_ticket_title',
        bodyKey: 'header.notif_support_ticket_body',
        bodyParams: { ticketNumber: data.ticketNumber, status: 'transferred' },
        route: ticketRoute(data),
      }),
      'support-ticket-transfer-requested': (data) => ({
        titleKey: 'header.notif_support_ticket_title',
        bodyKey: 'header.notif_support_ticket_body',
        bodyParams: { ticketNumber: data.ticketNumber, status: 'transfer-requested' },
        route: ticketRoute(data),
      }),
      'support-ticket-transfer-rejected': (data) => ({
        titleKey: 'header.notif_support_ticket_title',
        bodyKey: 'header.notif_support_ticket_body',
        bodyParams: { ticketNumber: data.ticketNumber, status: 'transfer-rejected' },
        route: ticketRoute(data),
      }),
    },
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    MessageService, // Global MessageService provider for toast notifications
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    { provide: CHAT_CONFIG, useValue: { apiUrl: `${environment.apiUrl}/chat`, socketUrl: environment.socketUrl ? `${environment.socketUrl}/ws` : undefined } },
    { provide: CHAT_AUTH, useExisting: AuthService },
    { provide: DOCS_CONFIG, useValue: { baseUrl: `${environment.apiUrl}/company/wiki`, publicUrl: `${environment.apiUrl}/public/wiki` } },
    { provide: SUPPORT_CONFIG, useValue: { baseUrl: `${environment.apiUrl}/company/support`, isResponderApp: true } },
    { provide: SUPPORT_AUTH, useExisting: AuthService },
    { provide: NOTIFICATION_CONFIG, useFactory: notificationConfigFactory },
    { provide: NOTIFICATION_AUTH, useExisting: AuthService },
    { provide: DOCUMENTS_TABLE_CONFIG, useFactory: documentsTableConfigFactory, deps: [HttpClient, FileUploadService] },
    { provide: USER_PROFILE_CONFIG, useFactory: userProfileConfigFactory, deps: [UsersService, CompanyUserSettingsService] },
    { provide: USER_TRANSFER_CONFIG, useFactory: userTransferConfigFactory, deps: [DriverTransferService] },
    { provide: USER_WALLET_CONFIG, useFactory: userWalletConfigFactory, deps: [CompanyPaymentService] },
    { provide: RECAPTCHA_SITE_KEY, useValue: environment.recaptchaSiteKey },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: getDefaultLanguage(),
        loader: {
          provide: TranslateLoader,
          useFactory: SimplifiedLoaderFactory,
          deps: [HttpClient, TranslationCacheService]
        }
      })
    )
  ]
};
